import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useEffect } from "react";
import clsx from "clsx";
import { useNativeDropzone } from "../../../utils/device";
import { FlipHorizontal, FlipVertical, ImageUp, RotateCcw, RotateCw } from "lucide-react";
import { Cropper, ImageRestriction } from 'react-advanced-cropper'
import { omit } from 'lodash';
import 'react-advanced-cropper/dist/style.css';
import 'react-advanced-cropper/dist/themes/corners.css';
import { AspectRatio } from "@mui/icons-material";
import { dataURLtoFile } from "../../../utils/file";

const useStyles = makeStyles((theme) => ({
    content: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        transition: "all 0.3s ease",
        overflow: "hidden",
    },

    cropperBox: {
        display: "flex",
        flexDirection: "column",

        [theme.breakpoints.up("sm")]: {
            flexDirection: "row",
        },
    },

    imageActions: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",

        [theme.breakpoints.up("sm")]: {
            flexDirection: "column",
        },
    },

    cropperWrapper: {
        padding: theme.spacing(2),
        display: "flex",
    },

    cropper: {
        width: theme.spacing(60),
        height: theme.spacing(60),
        borderRadius: theme.spacing(1),
        borderWidth: 2,
        borderStyle: "dashed",
        borderColor: theme.palette.border.primary,
        backgroundColor: `${theme.palette.border.primary}2f`,
        overflow: "hidden",
        position: "relative",

        [theme.breakpoints.down("md")]: {
            width: theme.spacing(50),
            height: theme.spacing(50),
        },

        [theme.breakpoints.down("sm")]: {
            width: theme.spacing(30),
            height: theme.spacing(30),
        },

        "& img": {
            width: "100%",
            height: "100%",
            objectFit: "cover",
        }
    },

    dropzone: {
        borderStyle: "dashed",
        borderRadius: theme.spacing(1),
        borderWidth: 2,
        borderColor: theme.palette.border.primary,
        backgroundColor: `${theme.palette.border.primary}2f`,
        padding: theme.spacing(2),
        width: "100%",
        height: "100%",
        minHeight: theme.spacing(20),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.3s ease",

        "&:focus": {
            outline: "none",
            borderColor: theme.palette.primary.main,
            backgroundColor: `${theme.palette.primary.main}33`,
        },

        "&:focus $dropzoneText": {
            color: theme.palette.primary.main,
        },

        "&:hover": {
            backgroundColor: `${theme.palette.primary.main}33`,
            borderColor: theme.palette.primary.main,
        },

        "&:hover $dropzoneText": {
            color: theme.palette.primary.main,
        }
    },

    dropzoneText: {
        color: theme.palette.text.secondary,
        margin: theme.spacing(1),
        transition: "color 0.3s ease",
    },

    saveButton: {
        backgroundColor: theme.palette.primary.main,

        "&:hover": {
            backgroundColor: theme.palette.primary.light,
        },
    }
}));

interface ImageCropDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit?: (croppedImage: File) => void;
    imageRestrictions?: {
        minWidth?: number;
        minHeight?: number;
        maxWidth?: number;
        maxHeight?: number;
        aspectRatio?: number | { minimum: number, maximum: number };
    }
}

export function ImageCropDialog(props: ImageCropDialogProps) {
    const { open, onClose, onSubmit, imageRestrictions = {} } = props;

    const s = useStyles();

    const [image, setImage] = React.useState<string | null>(null);
    const [fileName, setFileName] = React.useState<string | null>(null);
    const cropperRef = React.useRef(null);

    const {getRootProps, getInputProps} = useNativeDropzone({
        maxFiles: 1,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
        },
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                const file = acceptedFiles[0];
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImage(reader.result as string);
                    setFileName(file.name);
                };
                reader.readAsDataURL(file);
            }
        },
        onError: (error) => {
            console.error("Error uploading image:", error);
        },
    });

    const handleClose = () => {
        setImage(null);
        onClose();
    }

    const imageActions = [
        {
            icon: <FlipHorizontal />, 
            action: () => {
                if (cropperRef.current) {
                    cropperRef.current.flipImage(true, false);
                }
            }
        },
        {
            icon: <FlipVertical />, 
            action: () => {
                if (cropperRef.current) {
                    cropperRef.current.flipImage(false, true);
                }
            }
        },
        {
            icon: <RotateCw />,
            action: () => {
                if (cropperRef.current) {
                    cropperRef.current.rotateImage(90);
                }
            }
        },
        {
            icon: <RotateCcw />,
            action: () => {
                if (cropperRef.current) {
                    cropperRef.current.rotateImage(-90);
                }
            }
        }
    ]

    const handleSave = () => {
        if (cropperRef.current && onSubmit) {
            const croppedImage = cropperRef.current.getCanvas()?.toDataURL();
            if (croppedImage) {
                onSubmit(dataURLtoFile(croppedImage, fileName));
            }
        }
        handleClose();
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Upload Image</DialogTitle>
            <DialogContent className={s.content}>
                {image ? (
                    <Box className={s.cropperBox}>
                        <Box className={s.cropperWrapper} onResize={() => {
                            if (cropperRef.current) {
                                cropperRef.current.refresh();
                            }
                        }}>
                            <Cropper
                                ref={cropperRef}
                                src={image}
                                imageRestriction={ImageRestriction.fitArea}
                                className={clsx("cropper", s.cropper)}
                                stencilProps={{
                                    aspectRatio: imageRestrictions.aspectRatio,
                                    grid: true
                                }}
                                {...omit(imageRestrictions, ['aspectRatio'])}
                            />
                        </Box>
                        <Box className={s.imageActions}>
                            {imageActions.map((action, index) => (
                                <IconButton
                                    key={index}
                                    onClick={action.action}
                                    color="primary"
                                    size="large"
                                >
                                    {action.icon}
                                </IconButton>
                            ))}

                        </Box>
                    </Box>
                ) : (
                    <Box className={s.dropzone} {...getRootProps()}>
                        <input type="file" {...getInputProps()} />

                        <ImageUp size={48} className={s.dropzoneText} />
                        <Typography className={s.dropzoneText} variant="h6" color="textSecondary" textAlign="center">
                            Drag and drop an image here, or click to select one
                        </Typography>

                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ width: "100%", p: 2 }}>
                <Button variant="text" fullWidth onClick={handleClose} color="primary">
                    Cancel
                </Button>
                { image && (
                    <Button className={s.saveButton} variant="contained" fullWidth onClick={handleSave} color="primary">
                        Save
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}